import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";

/**
 * Trend strip for the Beauty Dashboard — plots the last N scans' overall
 * scores (skin, hair, beard) so users see momentum, not just the latest number.
 */
export default function BeautyTrends() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/beauty/scans");
        setScans((data.scans || []).slice().reverse().slice(-8));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || scans.length < 2) return null;

  const data = scans.map((s) => {
    const d = new Date(s.created_at);
    const scores = s.analysis?.overall_scores || {};
    return {
      label: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
      skin: scores.skin_health || 0,
      hair: scores.hair_health || 0,
      beard: scores.beard_health || 0,
    };
  });

  const hasBeardData = data.some(d => d.beard > 0);

  return (
    <section className="mt-10 border border-border p-6 lg:p-8" data-testid="beauty-trends">
      <p className="eyebrow flex items-center gap-2"><TrendingUp size={12} /> Progress · last {scans.length} scans</p>
      <div className="mt-4 h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="hsl(var(--border))" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
            <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
            <Line type="monotone" dataKey="skin"  name="Skin"  stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="hair"  name="Hair"  stroke="hsl(var(--foreground))" strokeWidth={2} dot={{ r: 3 }} />
            {hasBeardData && <Line type="monotone" dataKey="beard" name="Beard" stroke="#9ca3af" strokeWidth={2} dot={{ r: 3 }} />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <LegendDot color="hsl(var(--accent))" label="Skin" />
        <LegendDot color="hsl(var(--foreground))" label="Hair" />
        {hasBeardData && <LegendDot color="#9ca3af" label="Beard" />}
      </div>
    </section>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2.5 h-2.5" style={{ background: color }} /> {label}
    </span>
  );
}
